import { MarketNotReady } from './MarketScopeTabs';
import { useMarketReady } from './marketScope';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, EmptyState, ErrorState, Pager, Spinner, cx } from '@/components/ui';
import { changeColorClass, formatDate, formatPercent } from '@/lib/format';
import { ResultBadge } from './components';
import { ProfileTabs } from './ProfileTabs';
import { TradingNav } from './TradingNav';
import type { CalibrationBucket, Recommendation, TradingProfile } from './types';
import { useAnalysisPolicy, useJournal, useJournalStats } from './useTrading';
import { useRestingProfiles } from './usePaper';

/**
 * 지난 추천 — 이력과 누적 통계. (11단계 ⑤⑥)
 *
 * 🔴 이 화면의 숫자가 "이 전략이 실제로 작동하는가" 에 대한 유일한 답이다
 *   자동 종목 선별의 임계값도, LLM 분석의 품질도 결국 여기로 검증된다.
 *   추천 목록은 "오늘 무엇을 볼까" 이고, 이 화면은 "지금까지 맞았나" 다.
 *
 * 🔴 표본이 적을 때가 가장 위험하다
 *   추천이 사흘에 하루꼴로 나오고(31.3%, 43번 노트) 채점은 D+15 영업일이라,
 *   첫 결과가 나오기까지 3주가 걸린다. 그동안은 몇 건뿐이다.
 *
 *   1승 0패의 승률 100%와 30승 0패의 승률 100%를 같게 보여주면 안 된다.
 *   그래서 승률 옆에 모수를 반드시 붙이고, 표본이 적으면 그렇다고 적는다.
 */

/** 이 미만이면 승률을 참고용으로만 보라고 알린다. */
const SMALL_SAMPLE = 10;

const PAGE_SIZE = 20;

export function JournalPage() {
  const [page, setPage] = useState(0);

  /*
    프로필 필터. undefined 가 전체다. (17단계)

    🔴 통계와 목록에 같은 값을 쓴다
      따로 두면 "하이리스크 승률" 아래에 안전 추천 목록이 붙는다.
      목표 배수가 다른 두 집단이라 그 화면은 읽는 사람을 속인다.
  */
  const [profile, setProfile] = useState<TradingProfile | undefined>(undefined);
  const ready = useMarketReady();
  // 쉬는(잠정 중단) 추천 프로필 — 기록 화면이라 탭은 두고 "중단" 표식만 붙인다 (2026-10-10 사용자 · 141번 10.6)
  const ended = useRestingProfiles();

  /*
    🔴 준비 안 된 시장에서는 국내 추천 이력을 그리지 않고 끝낸다 (2026-09-20 사용자 지적)
      전에는 안내를 배너로 얹고 국내 이력을 그대로 아래에 그렸다.
      미국 추천이 생기면 isMarketReady 만 고치면 이 분기가 사라진다 (I-64).
   */
  if (!ready) {
    return (
      <div className="flex flex-col gap-4">
        <TradingNav />
        <MarketNotReady />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <TradingNav />

      <ProfileTabs
        value={profile}
        ended={ended}
        onChange={(next) => {
          setProfile(next);
          // 탭을 바꾸면 건수가 달라진다. 3페이지에 있다가 옮기면 빈 화면이 뜬다.
          setPage(0);
        }}
      />
      <StatsSection profile={profile} />
      <HistorySection page={page} onPageChange={setPage} profile={profile} />
      <AnalysisPolicySection />
    </div>
  );
}

// ─────────────────────────────────────────────
// AI 분석 기준 (3.12)
// ─────────────────────────────────────────────

/**
 * AI 에게 실제로 무엇을 시켰는지 보여준다.
 *
 * 🔴 프롬프트 원문을 그대로 띄운다. 요약하지 않는다
 *   "AI 는 이런 기준으로 분석합니다" 를 화면에 손으로 적어두면,
 *   프롬프트를 고쳤을 때 그 글이 남는다. 몇 달 뒤에는 화면의 설명과
 *   실제로 시킨 말이 다른데 아무도 그것을 모른다.
 *
 * 왜 이 화면에 두나
 *   "이 전략이 맞았나" 를 보는 자리 바로 아래다.
 *   승률이 낮게 나왔을 때 다음으로 묻게 되는 것이 "AI 가 뭘 보고 저렇게 썼나" 다.
 *
 * 접어두는 이유
 *   프롬프트가 40줄쯤 된다. 펼쳐두면 통계와 이력이 위로 밀린다.
 *   자주 볼 것은 아니고, 궁금할 때 있으면 되는 종류다.
 */
function AnalysisPolicySection() {
  const [open, setOpen] = useState(false);
  const query = useAnalysisPolicy();

  return (
    <Card title="AI 분석 기준">
      <p className="text-xs text-fg-muted">
        추천에 붙는 AI 분석은 종목을 고르지 않습니다. 지표로 이미 뽑힌 종목에 대해
        뉴스와 공시를 정리할 뿐입니다.
      </p>

      {!open && (
        <div className="mt-3">
          <Button variant="secondary" onClick={() => setOpen(true)}>
            무엇을 시켰는지 보기
          </Button>
        </div>
      )}

      {open && (
        <div className="mt-4">
          {query.isLoading && <Spinner label="불러오는 중" />}

          {query.error && (
            <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
          )}

          {query.data && (
            <>
              <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                <div className="flex gap-1">
                  <dt className="text-fg-muted">모델</dt>
                  <dd className="font-mono text-fg">{query.data.model}</dd>
                </div>
                <div className="flex gap-1">
                  <dt className="text-fg-muted">사고 강도</dt>
                  <dd className="font-mono text-fg">{query.data.effort}</dd>
                </div>
              </dl>

              <h3 className="mt-4 text-sm font-semibold text-fg">코드가 강제하는 것</h3>
              <p className="mt-1 text-xs text-fg-subtle">
                지시만으로는 부족해서 코드로 막아둔 것들입니다.
              </p>
              <ul className="mt-2 flex flex-col gap-2">
                {query.data.codeRules.map((rule) => (
                  <li key={rule} className="flex gap-2 text-xs leading-relaxed text-fg-muted">
                    <span aria-hidden="true" className="text-fg-subtle">
                      •
                    </span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>

              <h3 className="mt-4 text-sm font-semibold text-fg">모델에게 보내는 지시문</h3>
              <p className="mt-1 text-xs text-fg-subtle">
                요약이 아니라 실제로 보내는 원문입니다.
              </p>
              {/*
                whitespace-pre-wrap 으로 원문의 줄바꿈과 들여쓰기를 살린다.
                프롬프트는 목록 구조가 의미를 갖는다 — 뭉개면 읽기 어려워진다.

                🔴 pre 지만 글꼴은 본문 글꼴을 쓴다 (2026-08-30 사용자 피드백)
                  pre 의 기본 모노스페이스는 한글에서 글자 사이가 벌어져
                  읽기 나쁘다. 이 내용은 코드가 아니라 **문장**이라
                  본문 글꼴이 맞다. 들여쓰기는 공백 개수로 유지된다.

                줄간격과 안쪽 여백을 넉넉히 준다 - 폰에서 문단이 뭉쳐 보였다.
              */}
              <pre
                className={
                  'mt-2 overflow-x-auto whitespace-pre-wrap rounded-lg bg-surface-muted ' +
                  'p-4 font-sans text-[13px] leading-7 text-fg-muted'
                }
              >
                {query.data.systemPrompt}
              </pre>

              <div className="mt-3">
                <Button variant="ghost" onClick={() => setOpen(false)}>
                  접기
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────
// 누적 통계
// ─────────────────────────────────────────────

function StatsSection({ profile }: { profile: TradingProfile | undefined }) {
  const query = useJournalStats(profile);

  if (query.isLoading) {
    return <Spinner label="통계를 불러오는 중" />;
  }
  if (query.error) {
    return (
      <ErrorState
        message={query.error.message}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const stats = query.data;
  if (!stats) {
    return null;
  }

  /*
    아직 아무것도 채점되지 않은 상태.

    추천은 있는데 채점이 없는 기간이 반드시 존재한다 — D+15 영업일이라
    첫 추천으로부터 3주가 지나야 첫 결과가 나온다.
    그 기간에 "승률 -" 만 보여주면 고장으로 읽힌다. 왜 비어 있는지 적는다.
  */
  if (stats.evaluated === 0) {
    return (
      <Card title="누적 성적">
        <EmptyState
          title="아직 채점된 추천이 없습니다."
          description={
            stats.total === 0
              ? '추천이 쌓이면 여기에 승률과 손익률이 나옵니다.'
              : `추천 ${stats.total}건이 채점을 기다리고 있습니다. 추천일로부터 15영업일이 지나야 결과가 나옵니다.`
          }
        />
      </Card>
    );
  }

  const 표본부족 = stats.win + stats.loss < SMALL_SAMPLE;

  return (
    <Card title="누적 성적">
      {/*
        승률을 가장 크게 두되 모수를 바로 옆에 붙인다.
        따로 떨어뜨리면 큰 숫자만 읽고 넘어간다.
      */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-2xl font-semibold tabular text-fg">
          {stats.winRate === null ? '—' : `${stats.winRate}%`}
        </span>
        <span className="text-sm text-fg-muted">
          승률 · {stats.win}승 {stats.loss}패
          {stats.expired > 0 && ` · 미결 ${stats.expired}`}
        </span>
      </div>

      {표본부족 && (
        <p className="mt-2 text-xs text-fg-subtle">
          승패가 {stats.win + stats.loss}건뿐입니다. 이 승률로 전략을 판단하기에는 이릅니다.
        </p>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Stat label="채점 완료" value={`${stats.evaluated}건`} />
        <Stat label="채점 대기" value={`${stats.pending}건`} />
        <Stat
          label="평균 손익률"
          value={stats.avgReturn === null ? '—' : formatPercent(stats.avgReturn)}
          tone={stats.avgReturn}
        />
        <Stat
          label="최고 / 최저"
          value={
            stats.bestReturn === null || stats.worstReturn === null
              ? '—'
              : `${formatPercent(stats.bestReturn)} / ${formatPercent(stats.worstReturn)}`
          }
        />
      </dl>

      {/*
        🔴 미결을 패배로 세지 않았다는 것을 적어둔다.
          목표에도 손절에도 닿지 않고 15영업일이 지난 것이라
          이겼다고도 졌다고도 할 수 없다. 손실로 세면 승률이 실제보다 낮아진다.
          (16번 노트)
      */}
      <p className="mt-3 text-xs text-fg-subtle">
        승률은 승패가 갈린 건만으로 셉니다. 미결(15영업일 내 목표·손절 모두 미도달)은
        빠져 있습니다.
      </p>

      <CalibrationTable buckets={stats.calibration} />
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: number | null;
}) {
  return (
    <div>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd
        className={cx(
          'mt-0.5 text-sm font-medium tabular',
          tone === undefined || tone === null ? 'text-fg' : changeColorClass(tone),
        )}
      >
        {value}
      </dd>
    </div>
  );
}

// ─────────────────────────────────────────────
// 예상 적중도
// ─────────────────────────────────────────────

/**
 * 예상 적중도 — 예상 도달률과 실제 승률을 구간별로 맞춰 본다.
 *
 * 답하려는 질문은 하나다.
 * **예상 70%였던 건들의 실제 승률이 정말 70% 근처인가.**
 *
 * 어긋나면 백테스트가 과거를 잘못 재고 있다는 뜻이고,
 * 선별 조건이나 유사 구간 판정 기준을 고쳐야 한다.
 *
 * 표본 0인 구간은 숨긴다
 *   백엔드가 10%p 씩 열 칸을 항상 내려주는데, 초기에는 여덟 칸이 비어 있다.
 *   빈 줄이 여덟 개 있으면 실제로 값이 있는 두 줄이 묻힌다.
 *
 *   대신 각 줄에 표본 수를 적어 "이 구간이 3건짜리" 라는 것이 보이게 한다.
 *   어느 구간이 비었는지가 궁금해지는 시점은 표본이 충분히 쌓인 뒤이고,
 *   그때는 대부분의 칸이 차 있어 빈 칸이 오히려 눈에 띈다.
 */
function CalibrationTable({ buckets }: { buckets: CalibrationBucket[] }) {
  const 채워진구간 = buckets.filter((bucket) => bucket.samples > 0);

  if (채워진구간.length === 0) {
    return null;
  }

  return (
    <div className="mt-5 border-t border-line pt-4">
      <h3 className="text-sm font-semibold text-fg">예상 적중도</h3>
      <p className="mt-1 text-xs text-fg-muted">
        추천 시점의 예상 도달률과 실제로 목표에 닿은 비율을 견줍니다.
        둘이 가까울수록 예상이 믿을 만하다는 뜻입니다.
      </p>

      <ul className="mt-3 flex flex-col gap-2">
        {채워진구간.map((bucket) => {
          const 예상중앙 = (bucket.from + bucket.to) / 2;
          const 실제 = bucket.actualWinRate;

          return (
            <li key={`${bucket.from}-${bucket.to}`} className="flex items-center gap-3">
              <span className="w-20 shrink-0 text-xs tabular text-fg-muted">
                {bucket.from}~{bucket.to}%
              </span>

              {/*
                두 막대를 겹치지 않고 나란히 둔다.
                겹쳐 그리면 어느 쪽이 예상이고 어느 쪽이 실제인지 매번 범례를 봐야 한다.
              */}
              <div className="flex flex-1 flex-col gap-1">
                <Bar label="예상" percent={예상중앙} tone="muted" />
                <Bar label="실제" percent={실제} tone="brand" />
              </div>

              <span className="w-14 shrink-0 text-right text-xs tabular text-fg-subtle">
                {bucket.samples}건
              </span>
            </li>
          );
        })}
      </ul>

      <p className="mt-3 text-xs text-fg-subtle">
        예상 도달률은 과거 유사 구간에서 목표에 닿은 빈도입니다. 예측이 아닙니다.
      </p>
    </div>
  );
}

function Bar({
  label,
  percent,
  tone,
}: {
  label: string;
  percent: number | null;
  tone: 'muted' | 'brand';
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-6 shrink-0 text-[10px] text-fg-subtle">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-hover">
        {percent !== null && (
          <div
            className={cx('h-full', tone === 'brand' ? 'bg-brand' : 'bg-fg-subtle')}
            style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
          />
        )}
      </div>
      <span className="w-10 shrink-0 text-right text-[10px] tabular text-fg-muted">
        {percent === null ? '—' : `${Math.round(percent)}%`}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────
// 이력
// ─────────────────────────────────────────────

function HistorySection({
  page,
  onPageChange,
  profile,
}: {
  page: number;
  onPageChange: (page: number) => void;
  profile: TradingProfile | undefined;
}) {
  const query = useJournal(page, PAGE_SIZE, profile);

  if (query.isLoading) {
    return <Spinner label="이력을 불러오는 중" />;
  }
  if (query.error) {
    return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const data = query.data;
  if (!data || data.totalElements === 0) {
    return (
      <Card title="추천 이력">
        <EmptyState
          title="아직 추천이 없습니다."
          description="평일 21:05 자동 종목 선별이 조건을 통과한 종목을 여기에 남깁니다."
        />
      </Card>
    );
  }

  return (
    <Card title={`추천 이력 ${data.totalElements}건`}>
      <ul className="-mx-2 flex flex-col">
        {data.items.map((item) => (
          <JournalRow key={item.id} item={item} />
        ))}
      </ul>

      {/* 이동 UI 는 공용 Pager 다. 모의투자 청산·회고와 같은 모양이어야 한다 (2026-08-20) */}
      <Pager page={page} totalPages={data.totalPages} onChange={onPageChange} />
    </Card>
  );
}

/**
 * 이력 한 줄.
 *
 * 🔴 손익률을 결과 배지와 함께 둔다
 *   WIN 이어도 수수료·세금 전 목표가 기준이라 실제 체감과 다를 수 있다.
 *   배지만 보면 "이겼다" 로 끝나는데, 숫자가 옆에 있으면 얼마나 이겼는지가 남는다.
 */
function JournalRow({ item }: { item: Recommendation }) {
  return (
    <li>
      <Link
        to={`/trading/${item.id}`}
        className="flex items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-surface-muted"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-medium text-fg">{item.name}</span>
            <span className="font-mono text-xs text-fg-muted">{item.symbol}</span>
            <ResultBadge result={item.result} />
          </span>

          <span className="mt-0.5 block text-xs text-fg-muted">
            {formatDate(item.recommendedAt)} 추천 · {item.score.toFixed(1)}점
            {item.result === null && item.evaluateOn && ` · ${formatDate(item.evaluateOn)} 채점 예정`}
          </span>
        </span>

        <span className="shrink-0 text-right">
          {item.returnPercent === null ? (
            <span className="text-xs text-fg-subtle">—</span>
          ) : (
            <span className={cx('text-sm font-medium tabular', changeColorClass(item.returnPercent))}>
              {formatPercent(item.returnPercent)}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}
